import { uniformIntDistribution, xoroshiro128plus } from 'pure-rand';
import { RandomGenerator } from 'pure-rand/types/RandomGenerator';

// Re-export the type for use in other modules
export type { RandomGenerator };

// RNG state type
export type RngState = readonly number[];

/**
 * Create a new RNG from a seed
 */
export function createRng(seed: number): RandomGenerator {
  return xoroshiro128plus(seed);
}

/**
 * Restore an RNG from saved state
 */
export function createRngFromState(state: RngState): RandomGenerator {
  return xoroshiro128plus.fromState(state);
}

/**
 * Get the initial state for a new RNG with the given seed
 */
export function getInitialRngState(seed: number): RngState {
  return xoroshiro128plus(seed).getState();
}

/**
 * Generate a random integer in the range [min, max] (inclusive)
 */
export function randomInt(
  min: number,
  max: number,
  rng: RandomGenerator,
): [number, RandomGenerator] {
  return uniformIntDistribution(min, max, rng);
}

/**
 * Shuffle an array using the RNG
 */
export function shuffle<T>(
  array: T[],
  rng: RandomGenerator,
): [T[], RandomGenerator] {
  const ret = [...array];
  let j: number;
  let t: T;
  for (let i = 0; i < ret.length - 1; i++) {
    [j, rng] = uniformIntDistribution(i, ret.length - 1, rng);

    t = ret[i];
    ret[i] = ret[j];
    ret[j] = t;
  }
  return [ret, rng];
}

/**
 * Select a random element from an array
 */
export function randomSelect<T>(
  array: T[],
  rng: RandomGenerator,
): [T, RandomGenerator] {
  let j: number;
  [j, rng] = uniformIntDistribution(0, array.length - 1, rng);

  return [array[j], rng];
}

/**
 * Generate a seed from current time and Math.random
 */
export function generateSeed(): number {
  return Date.now() ^ (Math.random() * 0x100000000);
}
